import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Users, Award, Clock, Download, Search, BadgeCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import Navbar from "@/components/Navbar";
import { GolonganBadge } from "@/components/Badges";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOLONGAN_HEX = { Siaga: "#2E7D32", Penggalang: "#C62828", Penegak: "#F57F17", Pandega: "#4E342E" };
const BROWN = "#B85C38";

function StatCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-5 flex items-center gap-4">
      <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${tone}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <div className="font-display text-2xl font-extrabold text-[#1F1917]">{value}</div>
        <div className="text-xs uppercase tracking-widest font-bold text-[#8C827A]">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState({ golongan: [], kwarran: [] });
  const [scouts, setScouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ kwarran: "", golongan: "", q: "" });

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  const loadScouts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/scouts", { params });
      setScouts(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
    loadStats();
  }, [loadStats]);
  useEffect(() => { loadScouts(); }, [loadScouts]);

  const exportCsv = async (kind) => {
    try {
      const { data } = await api.get(`/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "scouts" ? "data_pramuka_depok.csv" : "prestasi_pramuka_depok.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Berhasil diekspor");
    } catch {
      toast.error("Gagal mengekspor");
    }
  };

  const selectCls = "rounded-lg border border-[#E5DFD7] bg-white px-3 py-2 text-sm outline-none focus:border-[#E5A93C]";

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-[#1F1917]">
              {user?.role === "admin" ? "Dashboard Kwarcab Depok" : "Dashboard Gudep"}
            </h1>
            <p className="text-[#8C827A] mt-1">Rekapitulasi data anggota & prestasi Gerakan Pramuka.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-export-excel-btn" onClick={() => exportCsv("scouts")}
              className="inline-flex items-center gap-2 bg-[#4A2E2B] text-white px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-[#3a2320] transition">
              <Download className="h-4 w-4" /> Data Pramuka (CSV)
            </button>
            <button data-testid="admin-export-prestasi-btn" onClick={() => exportCsv("achievements")}
              className="inline-flex items-center gap-2 bg-[#E5A93C] text-[#2A1F1B] px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-[#d69a2b] transition">
              <Download className="h-4 w-4" /> Prestasi (CSV)
            </button>
          </div>
        </div>

        {!stats ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#B85C38]" /></div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
              <StatCard testid="admin-stat-total-pramuka" icon={Users} label="Total Pramuka" value={stats.total_pramuka} tone="bg-[#4A2E2B]/10 text-[#4A2E2B]" />
              <StatCard testid="admin-stat-total-prestasi" icon={Award} label="Total Prestasi" value={stats.total_prestasi} tone="bg-[#E5A93C]/15 text-[#B85C38]" />
              <StatCard testid="admin-stat-verified" icon={BadgeCheck} label="Terverifikasi" value={stats.approved} tone="bg-emerald-100 text-emerald-700" />
              <StatCard testid="admin-stat-pending-verifikasi" icon={Clock} label={user?.role === "admin" ? "Menunggu Kwarcab" : "Menunggu Pembina"} value={user?.role === "admin" ? stats.validated + stats.pending : stats.pending} tone="bg-amber-100 text-amber-700" />
            </div>

            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6 lg:col-span-2">
                <h3 className="font-display font-semibold text-[#2A1F1B] mb-4">Distribusi Anggota per Kwarran</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.per_kwarran} margin={{ top: 5, right: 10, left: -10, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EBE3" vertical={false} />
                    <XAxis dataKey="name" angle={-40} textAnchor="end" height={60} tick={{ fontSize: 11, fill: "#8C827A" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8C827A" }} />
                    <Tooltip cursor={{ fill: "#F7F4EF" }} />
                    <Bar dataKey="value" name="Anggota" fill={BROWN} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6">
                <h3 className="font-display font-semibold text-[#2A1F1B] mb-4">Komposisi Golongan</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={stats.per_golongan.filter((g) => g.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                      {stats.per_golongan.map((g) => <Cell key={g.name} fill={GOLONGAN_HEX[g.name]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6 mb-8">
              <h3 className="font-display font-semibold text-[#2A1F1B] mb-4">Prestasi Terverifikasi per Tingkat</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.per_tingkat} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0EBE3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#8C827A" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8C827A" }} />
                  <Tooltip cursor={{ fill: "#F7F4EF" }} />
                  <Bar dataKey="value" name="Prestasi" fill="#E5A93C" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        <div className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm overflow-hidden">
          <div className="p-5 border-b border-[#E5DFD7] flex flex-wrap gap-3 items-center">
            <h3 className="font-display font-semibold text-[#2A1F1B] mr-auto">Registri Anggota</h3>
            <div className="relative">
              <Search className="h-4 w-4 text-[#8C827A] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="admin-search-input" className={selectCls + " pl-9"} placeholder="Cari nama / NTA / gudep"
                value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
            </div>
            <select data-testid="admin-filter-golongan" className={selectCls} value={filters.golongan} onChange={(e) => setFilters((f) => ({ ...f, golongan: e.target.value }))}>
              <option value="">Semua Golongan</option>
              {meta.golongan.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select data-testid="admin-filter-kwarran" className={selectCls} value={filters.kwarran} onChange={(e) => setFilters((f) => ({ ...f, kwarran: e.target.value }))}>
              <option value="">Semua Kwarran</option>
              {meta.kwarran.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table data-testid="admin-data-table" className="w-full text-sm">
              <thead>
                <tr className="bg-[#F7F4EF] text-left text-[#8C827A] text-xs uppercase tracking-widest">
                  <th className="px-5 py-3 font-bold">Nama</th>
                  <th className="px-5 py-3 font-bold">NTA</th>
                  <th className="px-5 py-3 font-bold">Golongan</th>
                  <th className="px-5 py-3 font-bold">Gudep</th>
                  <th className="px-5 py-3 font-bold">Kwarran</th>
                  <th className="px-5 py-3 font-bold">Dilantik</th>
                  <th className="px-5 py-3 font-bold text-center">Prestasi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#B85C38] inline" /></td></tr>
                ) : scouts.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-[#8C827A]">Belum ada anggota terdaftar.</td></tr>
                ) : scouts.map((s) => (
                  <tr key={s.id} className="border-t border-[#F0EBE3] hover:bg-[#FBF9F5]">
                    <td className="px-5 py-3 font-semibold text-[#1F1917]">{s.name}</td>
                    <td className="px-5 py-3 text-[#4A423F]">{s.nta}</td>
                    <td className="px-5 py-3"><GolonganBadge value={s.golongan} /></td>
                    <td className="px-5 py-3 text-[#4A423F]">{s.gudep || "—"}</td>
                    <td className="px-5 py-3 text-[#4A423F]">{s.kwarran || "—"}</td>
                    <td className="px-5 py-3 text-[#4A423F]">{s.tanggal_dilantik || "—"}</td>
                    <td className="px-5 py-3 text-center font-bold text-[#B85C38]">{s.prestasi_approved}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
